import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/auth.css';

const LOGO_URL = '/woss-publishing-assets/images/woss-publishing-logo-white.svg';

export default function Login() {
  const [step, setStep]         = useState('email');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const pwRef = useRef(null);

  useEffect(() => {
    if (step === 'password' && pwRef.current) {
      pwRef.current.focus();
    }
  }, [step]);

  const handleEmailContinue = (e) => {
    e.preventDefault();
    setError('');
    const v = email.trim();
    if (!v) { setError('Please enter your email address.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setError('Please enter a valid email address.'); return;
    }
    setStep('password');
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    if (!password) { setError('Please enter your password.'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token) {
        setError(data.message || 'We could not sign you in. Please verify your details and try again.');
        return;
      }
      sessionStorage.setItem('wp_token', data.token);
      window.location.href = '/app/portal/home';
    } catch {
      setError('Unable to reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goBackToEmail = () => {
    setStep('email');
    setError('');
    setPassword('');
    setShowPw(false);
  };

  return (
    <div className="wp-auth wp-auth--white">
      <main className="wp-auth__main">
        <div className="wp-auth__card wp-auth__card--white">

          <div className="wp-auth__logo-center">
            <Link to="/" aria-label="Woss Publishing — return to website">
              <img src={LOGO_URL} alt="Woss Publishing" className="wp-auth__logo wp-auth__logo--white" />
            </Link>
          </div>

          {step === 'email' ? (
            <>
              <h1 className="wp-auth__white-heading">WELCOME</h1>
              <p className="wp-auth__white-sub">Log in to Woss Publishing to continue.</p>

              {error && <div className="wp-auth__white-error" role="alert">{error}</div>}

              <form className="wp-auth__form" onSubmit={handleEmailContinue} noValidate>
                <div className="wp-auth__field wp-auth__field--white">
                  <label className="wp-auth__label--white" htmlFor="auth-email">
                    Email address *
                  </label>
                  <input
                    id="auth-email"
                    className="wp-auth__input--white"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    required
                    aria-required="true"
                  />
                </div>
                <button type="submit" className="wp-auth__btn--white">
                  CONTINUE
                </button>
              </form>

              <div className="wp-auth__white-links">
                <Link to="/" className="wp-auth__white-link">← Back to Woss Publishing</Link>
              </div>
            </>
          ) : (
            <>
              <h1 className="wp-auth__white-heading">ENTER YOUR PASSWORD</h1>
              <p className="wp-auth__white-sub">Enter your password for Woss Publishing to continue</p>

              <div className="wp-auth__email-chip--white">
                <span>{email}</span>
                <button type="button" className="wp-auth__chip-edit" onClick={goBackToEmail}>
                  Edit
                </button>
              </div>

              {error && <div className="wp-auth__white-error" role="alert">{error}</div>}

              <form className="wp-auth__form" onSubmit={handleSignIn} noValidate>
                <div className="wp-auth__field wp-auth__field--white" style={{ position: 'relative' }}>
                  <label className="wp-auth__label--white" htmlFor="auth-password">
                    Password *
                  </label>
                  <input
                    id="auth-password"
                    ref={pwRef}
                    className="wp-auth__input--white"
                    type={showPw ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    required
                    aria-required="true"
                    disabled={loading}
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    className="wp-auth__toggle--white"
                    onClick={() => setShowPw(v => !v)}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPw ? '●' : '○'}
                  </button>
                </div>

                <div className="wp-auth__white-links wp-auth__white-links--top">
                  <Link to="/auth/forgot-password" className="wp-auth__white-link" style={{ textDecoration: 'underline' }}>
                    Reset password
                  </Link>
                </div>

                <button type="submit" className="wp-auth__btn--white" disabled={loading}>
                  {loading ? 'Signing in…' : 'CONTINUE'}
                </button>
              </form>

              <div className="wp-auth__white-links">
                <button type="button" className="wp-auth__white-link" onClick={goBackToEmail}>
                  ← Back to login
                </button>
              </div>
            </>
          )}

        </div>
      </main>

      <footer className="wp-auth__footer wp-auth__footer--white">
        &copy; {new Date().getFullYear()} Woss Music Publishing Group LLC.{' '}
        <a href="/legal/terms" className="wp-auth__white-link">Terms of Use</a>
        {' · '}
        <a href="/legal/privacy" className="wp-auth__white-link">Privacy Policy</a>
      </footer>
    </div>
  );
}
